/**
 * A type-safe way of returning the keys of an object.
 * @param obj The object whose keys are required
 * @returns { Array<keyof T> | string[] } An array of object keys, where T is the type of obj
 * For objects with string index signatures, returns string[].
 * For objects with specific keys, returns Array<keyof T>.
 *
 * @example
 * // For objects with specific keys:
 * const obj = { a: 1, b: 2 };
 * const keys = typeSafeObjectKeys(obj); // type: ("a" | "b")[]
 *
 * @example
 * // For objects with string index signatures:
 * const obj: Record<string, number> = { a: 1, b: 2 };
 * const keys = typeSafeObjectKeys(obj); // type: string[]
 */
export const typeSafeObjectKeys = <T extends object>(
  obj: T,
): string extends keyof T ? string[] : Array<keyof T> => {
  // eslint-disable-next-line local-rules/no-object-keys
  return Object.keys(obj) as string extends keyof T ? string[] : Array<keyof T>;
};
